import { useMemo } from 'react';
import type { Absence, Appointment, ScheduleEntry, ScheduleResult, TaskListItem } from '@myapp/contract';
import { useSchedule } from '../../../entities/schedule';
import { useClockMinute } from '../../../shared/hooks/useClockMinute';
import { isWorkingDay } from '../../../shared/lib/workingDays';
import { buildSegments } from './buildSegments';
import { buildTooltip } from './blockTooltip';
import {
  clampSpanToShift,
  detectTransfers,
  packIntoLanes,
  resolveBlockTimes,
  rowHeightPx,
  shiftWindow,
  toBoardSpan,
  type BoardSpan,
  type LaneInput,
  type TransferInput,
} from '../../../shared/lib/schedule';
import type { BlockOwner, BoardBlock, BoardRowModel } from './boardTypes';

export type { BlockOwner, BlockSegment, BoardBlock, BoardRowModel } from './boardTypes';

/**
 * The board's one data hook: `GET /schedule` for a single day, reshaped into render-ready rows.
 *
 * `technicianIds` is deliberately omitted so the server returns the full roster — active technician
 * profiles plus anyone holding an entry in the window — already carrying name and avatar. That is
 * why the board needs no second request for a technician list.
 *
 * `exclude` drops the entry currently being rescheduled — a Termin or an Aufgabe — the same way
 * `CalendarPlanningOverlay` does it on the worker side. One filter, applied before lane packing, so
 * rendering, row height and the collision check all agree that the slot is free — otherwise the
 * draft standing in its place would be reported as colliding with itself.
 */
export interface BoardExclusion {
  kind: 'appointment' | 'task';
  /** The owner's id, never `schedule.id`. */
  ownerId: number;
}

export function useDispositionBoard(
  dayIso: string,
  from: string,
  to: string,
  exclude?: BoardExclusion | null,
) {
  const query = useSchedule({ from, to, includeWorkOrder: true });

  // Destructured so the memo keys on the two primitives — the caller rebuilds the object freely.
  const excludeKind = exclude?.kind ?? null;
  const excludeOwnerId = exclude?.ownerId ?? null;

  // Once a minute: a running block whose phase has overrun its plan grows to the clock.
  const nowMs = useClockMinute();

  const rows = useMemo(
    () => buildRows(query.data, dayIso, excludeKind, excludeOwnerId, nowMs),
    [query.data, dayIso, excludeKind, excludeOwnerId, nowMs],
  );

  return {
    rows,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/**
 * `ScheduleResult` → rows.
 *
 * `selectAllTechnicianSchedules` is not usable here: it splits entries into four per-kind arrays and
 * drops the per-entry timing for appointments and tasks. A board needs one flat, time-carrying list
 * per technician, so it walks `technicians[].entries` (already server-sorted) and joins to the
 * top-level lookup arrays by owner id.
 */
function buildRows(
  result: ScheduleResult | undefined,
  dayIso: string,
  excludeKind: BoardExclusion['kind'] | null,
  excludeOwnerId: number | null,
  nowMs: number,
): BoardRowModel[] {
  if (!result) return [];

  const appointmentsById = new Map(result.appointments.map((a) => [a.id, a]));
  const tasksById = new Map(result.tasks.map((t) => [t.id, t]));
  const absencesById = new Map(result.absences.map((a) => [a.id, a]));

  return result.technicians.map((technician) => {
    const visible: { block: Omit<BoardBlock, 'lane'>; laneInput: LaneInput }[] = [];

    const working = isWorkingDay(technician.workingDays, dayIso);
    const shift = shiftWindow(technician.shiftStart, technician.shiftEnd);

    /*
     * Who is a Transfer from whom, computed once up front — the Abfahrt side needs the *next*
     * entry, which the `forEach` below has not built a block for yet. Excludes the entry being
     * rescheduled, the same as the loop does, so a draft standing in for it cannot itself pair up.
     */
    const transfers = detectTransfers(
      technician.entries.flatMap((entry, index): TransferInput[] => {
        if (excludeOwnerId != null) {
          const ownerId = excludeKind === 'appointment' ? entry.appointmentId : entry.taskId;
          if (ownerId === excludeOwnerId) return [];
        }
        const live = resolveBlockTimes(entry, nowMs);
        return [
          {
            key: entryKey(entry, index),
            isJob: entry.type === 'APPOINTMENT' || entry.type === 'TASK',
            startMin: new Date(live.liveStartsAt).getTime(),
            travelMinutes: live.travelMinutes,
            departureMinutes: live.departureMinutes,
          },
        ];
      }),
    );

    technician.entries.forEach((entry, index) => {
      // Being rescheduled: the draft stands in for it, so it must not also draw, take a lane, or
      // collide with the draft that replaced it.
      if (excludeOwnerId != null) {
        const ownerId = excludeKind === 'appointment' ? entry.appointmentId : entry.taskId;
        if (ownerId === excludeOwnerId) return;
      }

      // Drawn between the live edges: a block whose Anfahrt really left at 16:35 starts there,
      // and an open phase that has run past its plan stretches to the clock. Lanes and the drag
      // collision read the same span — the technician *is* there, and the server will reserve
      // that time the moment the phase closes.
      const live = resolveBlockTimes(entry, nowMs);
      const span = toBoardSpan(live.liveStartsAt, live.liveEndsAt, dayIso);
      if (!span) return; // entirely outside the drawn window

      // An absence is stored as whole calendar days — a Krankmeldung runs local midnight to local
      // midnight — so drawn literally it covers the board from 06:00 to 22:00 and claims hours the
      // technician was never going to work. It belongs on the shift, which is what the worker
      // calendar already does (`buildDayBlocks`). Appointments and tasks are left alone: one running
      // past shift end is real overtime, and hiding it would be worse than the problem.
      const drawn = entry.type === 'ABSENCE' ? toAbsenceSpan(span, working, shift) : span;
      if (!drawn) return;

      const appointment =
        entry.appointmentId != null ? appointmentsById.get(entry.appointmentId) ?? null : null;
      const task = entry.taskId != null ? tasksById.get(entry.taskId) ?? null : null;
      const absence = entry.absenceId != null ? absencesById.get(entry.absenceId) ?? null : null;

      const key = entryKey(entry, index);
      const kinds = {
        travel: transfers.travel.has(key) ? ('Transfer' as const) : ('Anfahrt' as const),
        departure: transfers.departure.has(key) ? ('Transfer' as const) : ('Abfahrt' as const),
      };

      visible.push({
        block: {
          key,
          kind: entry.type,
          entry,
          // The join is already done above; keeping the result is what lets the hover card read the
          // work order without a second request. `buildOwner` picks by which FK resolved, not by
          // `entry.type`, so an entry whose owner is missing from the payload stays `null`.
          owner: buildOwner(appointment, task, absence),
          span: drawn,
          segments: buildSegments(entry, drawn, appointment, task, absence, nowMs, kinds),
          tooltip: buildTooltip(entry, appointment, task, absence),
          status: appointment?.status ?? task?.status ?? null,
          needsRescheduling: entry.needsRescheduling,
          technicianCount: absence?.technicians.length ?? 1,
        },
        laneInput: { key, span: drawn },
      });
    });

    const { lanes, laneCount } = packIntoLanes(visible.map((v) => v.laneInput));

    return {
      technicianId: technician.technicianId,
      technicianName: technician.technicianName,
      technicianAvatar: technician.technicianAvatar,
      workingDays: technician.workingDays,
      trades: technician.trades,
      calendarHidden: technician.calendarHidden,
      shiftStart: technician.shiftStart,
      shiftEnd: technician.shiftEnd,
      // Already computed above for the absence clamp — the drag's shift check reads it from here
      // instead of re-parsing the two ISO strings on every pointer move.
      shift,
      blocks: visible.map(({ block }) => ({ ...block, lane: lanes.get(block.key) ?? 0 })),
      rowHeight: rowHeightPx(laneCount),
    };
  });
}

/**
 * One block's identity for both lanes and transfer detection — a **string**, deliberately (see
 * `packIntoLanes`). `entry.id` is nullable only for a projected Pause, hence the index fallback.
 */
function entryKey(entry: ScheduleEntry, index: number): string {
  return `${entry.type}:${entry.id ?? `p${index}`}`;
}

/**
 * Which of the three lookups resolved, as a discriminated union the card can switch on.
 *
 * At most one is ever non-null: an entry carries exactly one owner FK.
 */
function buildOwner(
  appointment: Appointment | null,
  task: TaskListItem | null,
  absence: Absence | null,
): BlockOwner | null {
  if (appointment) return { kind: 'appointment', appointment };
  if (task) return { kind: 'task', task };
  if (absence) return { kind: 'absence', absence };
  return null;
}

/**
 * Where an absence is drawn: inside the technician's shift, never across the whole board.
 *
 * `null` means draw nothing —
 *  - the technician does not work that day, so there is no work time for the absence to occupy and
 *    the row is already shaded grey end to end;
 *  - or the absence falls entirely outside the shift, e.g. a 05:00–06:30 Arzttermin.
 *
 * With no shift on the profile there is nothing to clamp to, so the span is left as it is rather
 * than guessed at.
 */
function toAbsenceSpan(
  span: BoardSpan,
  working: boolean,
  shift: { startMin: number; endMin: number } | null,
): BoardSpan | null {
  if (!working) return null;
  if (!shift) return span;
  return clampSpanToShift(span, shift);
}

/**
 * Re-exported so the block UI keeps importing it from here. The implementation moved to
 * `blockTooltip.ts` when the week view needed the same two helpers.
 */
export { buildReschedulingLine } from './blockTooltip';
